import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsCard from "@/components/base/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { newsCategories, newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 5;

export default function News() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useSeo({
    title: "影视资讯 - 姐姐的朋友",
    description:
      "姐姐的朋友影视资讯频道，汇集行业动态、新片报道与主创访谈，提供影视行业观察、新片幕后故事与创作者深度对话。",
    keywords: "影视资讯,行业动态,新片报道,明星访谈,影视新闻,姐姐的朋友",
  });

  const filtered = useMemo(
    () => (category === "全部" ? newsList : newsList.filter((item) => item.category === category)),
    [category]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeCategory = (next: string) => {
    setCategory(next);
    setPage(1);
  };

  const hotNews = newsList.slice(1, 5);

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-9">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

        <div className="mt-4 border-b border-background-300 pb-6">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">
            行业观察、新片报道与主创访谈。我们关注作品如何被拍出来，也关注它为什么被需要。
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              {["全部", ...newsCategories].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => changeCategory(item)}
                  className={[
                    "cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors",
                    category === item
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600",
                  ].join(" ")}
                >
                  {item}
                </button>
              ))}
              <span className="ml-auto font-label text-xs text-foreground-500">共 {filtered.length} 篇</span>
            </div>

            {visible.length === 0 ? (
              <div className="mt-6">
                <EmptyState
                  title="暂无影视资讯，敬请期待"
                  description="这个分类下暂时还没有文章，你可以切换到其他分类查看最新内容。"
                  actionLabel="查看全部资讯"
                  onAction={() => changeCategory("全部")}
                />
              </div>
            ) : (
              <div className="mt-3 border-t border-background-200">
                {visible.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>

          <aside className="lg:col-span-4">
            <div className="flex flex-col gap-6 lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    热门阅读
                  </h2>
                  <span className="font-label text-[11px] text-foreground-400">TRENDING</span>
                </div>
                <div className="mt-2">
                  {hotNews.map((item, index) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex cursor-pointer gap-3 border-b border-background-200 py-3 last:border-b-0"
                    >
                      <span
                        className={[
                          "w-5 shrink-0 font-label text-sm font-bold",
                          index === 0 ? "text-accent-600" : "text-foreground-300",
                        ].join(" ")}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm leading-6 text-foreground-800 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </p>
                        <p className="mt-1 font-label text-[11px] text-foreground-400">{item.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="text-sm font-bold text-foreground-950">资讯分类</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {newsCategories.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => changeCategory(item)}
                      className="cursor-pointer rounded-full bg-background-100 px-3 py-1.5 text-[11px] text-foreground-600 transition-colors hover:bg-secondary-200"
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-xs leading-6 text-foreground-500">
                  想投稿或提供行业线索？发送邮件至
                  <a href="mailto:hello@jiejiedepengyou.com" className="ml-1 cursor-pointer text-primary-600 hover:underline">
                    hello@jiejiedepengyou.com
                  </a>
                </p>
              </div>

              <div className="rounded-lg bg-primary-950 p-5 text-background-50">
                <p className="font-label text-[10px] tracking-[0.24em] text-primary-400">WEEKLY</p>
                <h2 className="mt-2 text-sm font-bold">每周三更新行业观察</h2>
                <p className="mt-2 text-xs leading-6 text-primary-200">
                  从排片、制作到观众讨论，我们只记录值得留下的部分。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}